import { TbChevronDown, TbChevronRight, TbQuote, TbX } from "solid-icons/tb";
import { type Component, createSignal, For, Show } from "solid-js";
import { linkifyText } from "../util";

interface TranscriptPastedTextProps {
  /** Stamped as `data-media-id` so a host can scroll this row into view by id. */
  mediaId?: string;
  text: string;
  /** While true, the block renders dimmed and without controls. */
  pending?: boolean;
  /**
   * Forces a collapsed block open. A find bar can only match text that is in
   * the DOM, so a surface with one opens what it collapsed while a query runs.
   */
  forceExpanded?: boolean;
  /**
   * When provided (and not pending), a hover-revealed remove control is
   * rendered. This is the one transcript item whose content came off the
   * clipboard, so a mis-paste has to be removable.
   */
  onRemove?: () => void;
  /**
   * Opens a pasted http(s) URL. A host webview that can't navigate a plain
   * anchor (chief → App.OpenURL) passes this to route the click; on the web
   * viewer it is omitted and the anchor navigates natively.
   */
  onOpenUrl?: (url: string) => void;
}

const linkClass = "text-accent underline underline-offset-2 hover:no-underline";

// Past either ceiling the block becomes collapsible: a document or canvas pushed
// into the session arrives as one paste of the whole thing, and at full height it
// buries the conversation it was pinned to.
const COLLAPSE_OVER_CHARS = 240;
const COLLAPSE_OVER_LINES = 2;

// Newlines are preserved — a paste arrives with the line breaks it had wherever
// it was copied from, and collapsing them turns a list or a stack trace into one
// run-on line.
const PastedQuote: Component<{ text: string; pending?: boolean; onOpenUrl?: (url: string) => void }> = (props) => (
  <blockquote
    classList={{ "opacity-60": props.pending }}
    class="border-l-2 border-accent py-0.5 pr-7 pl-3 text-sm leading-relaxed wrap-break-word whitespace-pre-wrap text-primary-text transition-opacity"
  >
    <For each={linkifyText(props.text)}>
      {(segment) =>
        segment.type === "link" ? (
          <a
            href={segment.value}
            draggable={false}
            target="_blank"
            rel="noopener noreferrer"
            class={linkClass}
            onClick={(e) => {
              if (props.onOpenUrl) {
                e.preventDefault();
                props.onOpenUrl(segment.value);
              }
            }}
          >
            {segment.value}
          </a>
        ) : (
          segment.value
        )
      }
    </For>
  </blockquote>
);

// Presentational only: text a participant typed or pasted into the transcript,
// rendered as a quoted block. A long paste starts open as a scrollable,
// height-capped copy of the text (someone who pastes a document wants it read,
// not hunted for) and can be collapsed by hand to a one-line summary.
export const TranscriptPastedText: Component<TranscriptPastedTextProps> = (props) => {
  const [expanded, setExpanded] = createSignal(true);
  const lines = () => props.text.split("\n");
  const collapsible = () => props.text.length > COLLAPSE_OVER_CHARS || lines().length > COLLAPSE_OVER_LINES;
  const open = () => expanded() || Boolean(props.forceExpanded);
  const summary = () => (lines().find((line) => line.trim() !== "") ?? "").trim();

  return (
    <div data-media-id={props.mediaId} class="group/pasted relative my-2">
      <Show
        when={collapsible()}
        fallback={<PastedQuote text={props.text} pending={props.pending} onOpenUrl={props.onOpenUrl} />}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={open()}
          title={open() ? "Hide the pasted text" : "Show the pasted text"}
          classList={{ "opacity-60": props.pending }}
          class="flex w-full min-w-0 items-center gap-1.5 pr-7 text-xs text-muted-text transition-opacity hover:text-primary-text focus:ring-2 focus:ring-brand focus:outline-none"
        >
          <Show when={open()} fallback={<TbChevronRight class="size-3.5 shrink-0" aria-hidden="true" />}>
            <TbChevronDown class="size-3.5 shrink-0" aria-hidden="true" />
          </Show>
          <TbQuote class="size-3.5 shrink-0 text-accent/70" aria-hidden="true" />
          <span class="min-w-0 flex-1 truncate text-left text-primary-text">{summary()}</span>
          <span class="shrink-0 tabular-nums">{lines().length} lines</span>
        </button>
        <Show when={open()}>
          <div class="mt-1 max-h-56 overflow-y-auto">
            <PastedQuote text={props.text} pending={props.pending} onOpenUrl={props.onOpenUrl} />
          </div>
        </Show>
      </Show>
      <Show when={!props.pending && props.onRemove}>
        {/* Revealed on focus as well as hover, so it is reachable from the keyboard. */}
        <button
          type="button"
          aria-label="Remove from session"
          title="Remove from session"
          onClick={() => props.onRemove?.()}
          class="absolute top-0 right-0 hidden rounded-full border border-primary-border bg-canvas/90 p-1 text-primary-text group-focus-within/pasted:block group-hover/pasted:block hover:bg-panel-hover focus:ring-2 focus:ring-brand focus:outline-none"
        >
          <TbX class="size-4" />
        </button>
      </Show>
    </div>
  );
};
